import { useTheme } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { memo, useCallback } from 'react';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import { SearchEntityPeekHost } from 'src/components/EntitySidePanel';
import { ProjectMenu } from 'src/components/ProjectComponents/project-menu';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { Address, Client, Conversation, Project } from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { AnySearchableEntity } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { ClientSidePanel } from 'src/pages/Clients/SidePanel/side-panel';
import { ConversationWindow } from 'src/pages/Conversations';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { ProjectSidePanel } from 'src/pages/Projects/SidePanel/side-panel';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { useCustomView, useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

// SiteSidePanel is a bare card column now, so this host owns the chrome, the scroller
// and the sticky slot the floating personalize tool portals into.
const SearchSitePanel = ({ site, onClose }: { site: Address; onClose: () => void }) => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const customViewId = useCustomView(EntityNames.Site)?.customViewId;

  return (
    <SearchEntityPeekHost
      onOpenFullPage={() => {
        navigate(portalPaths.site.full(site.id), { state: { customViewId } });
      }}
      menu={isMobile ? null : <SiteMenu address={site} />}
      onClose={onClose}
      closeTooltip="Close Site Details"
    >
      {(toolsSlot) => <SiteSidePanel site={site} onClose={onClose} toolsSlot={toolsSlot} />}
    </SearchEntityPeekHost>
  );
};

// ClientSidePanel is a bare card column now, so this host owns the chrome, the scroller
// and the sticky slot the floating personalize tool portals into.
const SearchClientPanel = ({ client, onClose }: { client: Client; onClose: () => void }) => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const customViewId = useCustomView(EntityNames.Client)?.customViewId;

  return (
    <SearchEntityPeekHost
      onOpenFullPage={() => {
        navigate(portalPaths.client.full(client.clientId), { state: { customViewId } });
      }}
      menu={isMobile ? null : <ClientMenu clientId={client.clientId} />}
      onClose={onClose}
      closeTooltip="Close Client Details"
    >
      {(toolsSlot) => (
        <ClientSidePanel clientId={client.clientId} onClose={onClose} toolsSlot={toolsSlot} />
      )}
    </SearchEntityPeekHost>
  );
};

// ProjectSidePanel is a bare card column now, so this host owns the chrome, the scroller
// and the sticky slot the floating personalize tool portals into.
const SearchProjectPanel = ({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) => {
  const { isMobile } = useScreenDimensions();

  return (
    <SearchEntityPeekHost
      menu={
        isMobile ? null : (
          <ProjectMenu
            address={project.address as Address}
            project={project}
            onDelete={onClose}
          />
        )
      }
      onClose={onClose}
      closeTooltip="Close Project Details"
    >
      {(toolsSlot) => (
        <ProjectSidePanel project={project} onClose={onClose} toolsSlot={toolsSlot} />
      )}
    </SearchEntityPeekHost>
  );
};

const GenericSidePanelComponent = ({
  entityName,
  selectedRecord,
  handleRecordClose,
}: {
  entityName: EntityNames;
  selectedRecord: AnySearchableEntity | null;
  handleRecordClose: () => void;
}) => {
  const theme = useTheme();
  const { updateConversation } = useConversationUpdate();

  const onUpdate = useCallback(
    async (conversation: Conversation) => {
      await updateConversation(conversation);
    },
    [updateConversation],
  );

  useOnEsc(handleRecordClose);

  if (!selectedRecord) {
    return;
  }

  if (entityName === EntityNames.Conversation) {
    return (
      <EntityWindowWrapper>
        <ConversationWindow
          onUpdate={onUpdate}
          conversation={selectedRecord as Conversation}
          onCloseConversation={handleRecordClose}
          isSplitView
        />
      </EntityWindowWrapper>
    );
  }

  if ([EntityNames.Client, EntityNames.Site, EntityNames.Project].includes(entityName)) {
    return (
      <EntityWindowWrapper
        sx={{
          [theme.breakpoints.up('sm')]: {
            minWidth: '400px',
          },
        }}
      >
        {entityName === EntityNames.Client && (
          <SearchClientPanel client={selectedRecord as Client} onClose={handleRecordClose} />
        )}
        {entityName === EntityNames.Site && (
          <SearchSitePanel site={selectedRecord as Address} onClose={handleRecordClose} />
        )}
        {entityName === EntityNames.Project && (
          <SearchProjectPanel
            project={selectedRecord as Project}
            onClose={handleRecordClose}
          />
        )}
      </EntityWindowWrapper>
    );
  }

  return null;
};

export const GenericSidePanel = memo(GenericSidePanelComponent);
